import { CellData, SortColumn } from '../../../API/APIObjects';
import { RowsPerPageOptions } from '../../../Contexts/FilterProvider/FilterProvider.types';

// TODO more responsive design for mobile

export const DEFAULT_ROWS_PER_PAGE: number = RowsPerPageOptions[25];

export enum ColumnType {
    /**
     * Displays content as is
     */
    Normal = 1,
    /**
     * MM/DD/YY, HH:MM AM/PM
     */
    Date,
    /**
     * Y for true blank for false
     */
    Boolean,
    /**
     * Y for true, N for false
     */
    BooleanYN,
    /**
     * Check for true, blank for false
     */
    BooleanCheck,
    /**
     * Check for 'true', X for 'false', blank for others
     */
    BooleanString,
    /**
     * ###-###-####
     */
    Phone,
    /**
     * Displays a comma separated list
     */
    List,
    /**
     * Displays a list with each entry on a new line in the column
     */
    ListMultiLine,
    /**
     * Cell value is used as src for img element
     */
    Image,
    /**
     * #.##.##
     */
    VersionNumber,
    /**
     * Formats cell value as currency
     */
    Currency,
    /**
     * Formats cell value as a percent. Expects the cell value to be a decimal
     */
    Percent,
    /**
     * Formats cell value using capitalization logic for individual names, not business
     */
    IndividualName
}

export interface RowData {
    [columnName: string]: CellData;
}

export interface ColumnData {
    /**
     * Unique id for the column
     */
    id: number;
    /**
     * The property name of the row data corresponding to the column's cell value
     */
    rowDataKey: string;
    /**
     * The property name of the row data to be displayed on the line below the primary row data within the same cell (if it exists).
     */
    rowSecondaryDataKey?: string;
    /**
     * Class applied to the {@link ColumnData.rowSecondaryDataKey rowSecondaryDataKey} line, for when it should read as
     * secondary to the value above it rather than matching it.
     */
    secondaryDataClassName?: string;
    /**
     * Column label
     */
    label: string;
    /**
     * Overrides the column type's cell alignment.
     */
    cellJustification?: 'left' | 'center' | 'right';
    /**
     * The table header row cell justification. Default th default (-internal-center)
     */
    headerJustification?: 'left' | 'center' | 'right';
    /**
     * Callback when row column cell is clicked. Will override the onRowClick of the table
     */
    onCellClick?: (data: RowData) => void;
    /**
     * Suggestive width. The columns will stretch to fill 100% of the container's width. This property
     * resizes columns in relation to each other.
     */
    percentWidth: number;
    /**
     * Minimum width for the cell. Default 100px
     */
    minWidth?: string;
    /**
     * Determines cell formatting. Default 'Normal'
     */
    type?: ColumnType;
    /**
     * Font weight of the cell. Default 'normal'.
     */
    fontWeight?: 'normal' | 'bold';
    /**
     * What to display in the table cell if the cell data is an empty string, null, or undefined
     */
    replaceWithifEmpty?: CellData;
    /**
     * Limits the cell data to the passed in number of lines. An ellipsis is displayed if the content
     * is longer than the allotted line count.
     */
    lineLimit?: number;
    /**
     * By default, all columns are sortable if the sorting prop is passed to the table.
     * This will disable sorting for the specific column.
     */
    disableSorting?: boolean;
    /**
     * Overrides the cell formatting associated with the column type
     */
    customCellFormatter?: (cell: CellData, row: RowData) => CellData;
    /**
     * Class applied to this column's value cells (body rows and pinned summary rows), not its header cell.
     * Lets a table pad or style one kind of column, e.g. numbers, without affecting the others.
     */
    cellClassName?: string;
}

export interface TableProps {
    /**
     * Whether the table should be striped according to the theme
     */
    themed?: boolean;
    /**
     * Removes cell borders
     */
    borderless?: boolean;
    /**
     * Padding for table cells. Default 'normal'
     */
    cellPadding?: 'normal' | 'extra';
    /**
     * Where a sortable header's sort icon sits. Default 'end'.
     * - 'end': label stretches and the icon sits at the far end of the cell.
     * - 'afterLabel': icon sits right beside its label, so it reads as this column's rather than the next one's.
     */
    sortIconPlacement?: 'end' | 'afterLabel';
    /**
     * Unique name for the table. Used for unique keys
     */
    name: string;
    /**
     * Column data
     */
    columns: ColumnData[];
    /**
     * Row data
     */
    rows: RowData[] | undefined;
    /**
     * Callback for when row is clicked
     */
    onRowClick?: (data: RowData) => void;
    /**
     * Whether column sorting should be enabled. Sort icons are for feedback purposes only,
     * the table component does not handle sorting by itself.
     */
    sorting?: {
        /**
         * The sort columns. Pririority is given to those first in the list.
         * Doesn't actually sort the table. Similar to pagination, sorting
         * should perform an API call
         */
        sortColumns: SortColumn[];
        /**
         * Callback when column is clicked. Should perform API call
         *
         * @param columns the columns to sort by
         */
        onSort: (columns: SortColumn[]) => void;
    };
    /**
     * All rows provided in the rows prop are displayed, these props are for user feedback.
     * Pagniation is handled server side, so the page/rowsPerPage properties don't actually modify
     * the list of options we are displaying since the server will only give us 25 rows if that's
     * what we ask for.
     *
     * An API call should be made when pagination changes to fetch new data.
     *
     * No pagination will be displayed if property is not set
     */
    pagination?: {
        /**
         * Callback when pagination values change.
         *
         * @param page the new page
         * @param rowsPerPage the new number of rows per page
         */
        onPageChange: (page: number, rowsPerPage: number) => void;
        /**
         * The total number of rows
         */
        totalCount: number;
        /**
         * Current page number
         */
        page: number;
        /**
         * Rows per page to display
         */
        rowsPerPage: number;
    };
    // TODO: Change footer to only use a columns array and have each ColumnData have a footer value that can be used to simplify the footer.

    /**
     * When defined, add a totals row to the bottom of the table.
     */
    addTotalRow?: {
        /**
         * Row data for the total row.
         */
        rows?: RowData[];
        /**
         * Column data for the total row.
         */
        columns?: ColumnData[];
    };
    /**
     * Emphasized rows (e.g. Total and Average) pinned beneath the header, above the data rows. Set `disableSorting` on
     * the summary columns unless clicks there should sort - they sit right under the header and are easy to hit by mistake.
     */
    topSummaryRows?: {
        /**
         * Row data for the pinned summary rows.
         */
        rows: RowData[];
        /**
         * Column data for the pinned summary rows.
         */
        columns: ColumnData[];
        /**
         * Indexes into {@link rows} rendered bold instead of the summary block's regular weight (e.g. the Total row).
         */
        emphasizedRowIndexes?: number[];
    };
    /**
     * A string of any additional information that is to be displayed above the row that shows how many results you have.
     */
    additionalInformation?: string;
}